/**
 * Recovery for the edge auth gate — the oauth2-proxy that fronts every host in
 * the PR preview environments.
 *
 * Once its session cookie expires the gate answers unauthenticated requests with
 * a cross-origin 302 to the identity provider. A navigation would follow that
 * redirect, but the Angular service worker answers navigations from the cached
 * `index.html` without touching the network, so the redirect never reaches the
 * browser and the app boots against a dead session. Worse, the plain `fetch` the
 * booting app makes for its runtime config cannot follow a cross-origin redirect
 * either, so ngsw reports its synthetic `504 Gateway Timeout` instead.
 *
 * The way out is to take the service worker out of the loop for one request:
 * `ngsw-bypass` makes the redirect observable, and a full-page navigation
 * carrying the same marker lets the browser perform the sign-in redirect itself.
 *
 * See docs/dev/docs/deployment/2026-08-05-pr-preview-deployments.md.
 */

export const NGSW_BYPASS_PARAM = 'ngsw-bypass';

/**
 * A gated path that is cheap to fetch and always reflects the gate's verdict.
 * Deliberately not `/ngsw.json` or `/ngsw-worker.js`: those are exempted from the
 * gate so a stuck client can still pull a fresh worker, so they answer 200 even
 * when the gate is shut.
 */
const PROBE_PATH = '/assets/config.json';

/** The probe URL: outside the service worker, and immune to any cache. */
export function edgeAuthGateProbeUrl(origin: string, now: number): string {
  const probe = new URL(PROBE_PATH, origin);
  probe.searchParams.set(NGSW_BYPASS_PARAM, 'true');
  probe.searchParams.set('t', now.toString());
  return probe.toString();
}

/**
 * The URL to reload to so the browser, rather than the service worker, handles
 * the gate's redirect. Null when this page was already loaded through a bypass:
 * the gate is then shut for a reason a reload cannot fix, and retrying would
 * spin forever.
 */
export function bypassUrlFor(href: string): string | null {
  const target = new URL(href);

  if (target.searchParams.get(NGSW_BYPASS_PARAM) === 'true') {
    return null;
  }

  target.searchParams.set(NGSW_BYPASS_PARAM, 'true');
  return target.toString();
}

/**
 * The same URL without the bypass marker, or null if there was none. A URL
 * pinned to `ngsw-bypass` would silently opt out of the service worker on every
 * future visit, so it must not survive into a bookmark.
 */
export function markerStrippedUrl(href: string): string | null {
  const current = new URL(href);

  if (!current.searchParams.has(NGSW_BYPASS_PARAM)) {
    return null;
  }

  current.searchParams.delete(NGSW_BYPASS_PARAM);
  return `${current.pathname}${current.search}${current.hash}`;
}

/**
 * Whether the edge auth gate is redirecting us to the identity provider.
 *
 * `redirect: 'manual'` is what makes this observable: it turns the gate's 302
 * into an opaque-redirect response instead of a cross-origin fetch failure that
 * is indistinguishable from being offline.
 */
export async function edgeAuthGateIsClosed(): Promise<boolean> {
  try {
    const response = await fetch(
      edgeAuthGateProbeUrl(window.location.origin, Date.now()),
      { redirect: 'manual', cache: 'no-store' },
    );
    return response.type === 'opaqueredirect';
  } catch {
    // Genuinely unreachable. Being offline is not the gate's doing, and reloading
    // would only replace a working offline shell with an error page.
    return false;
  }
}

/** Reloads past the service worker so the browser runs the sign-in flow. */
export function reloadThroughEdgeAuthGate(): boolean {
  const target = bypassUrlFor(window.location.href);

  if (target == null) {
    return false;
  }

  window.location.replace(target);
  return true;
}

export interface EdgeAuthGateRecoveryDeps {
  isClosed: () => Promise<boolean>;
  reload: () => boolean;
}

/**
 * Builds the "did the gate claim this failure, and if so get us past it" entry
 * point. Exported as a factory so its state can be exercised in isolation; the
 * app uses the {@link recoverThroughEdgeAuthGateIfClosed} singleton.
 */
export function createEdgeAuthGateRecovery(
  { isClosed, reload }: EdgeAuthGateRecoveryDeps = {
    isClosed: edgeAuthGateIsClosed,
    reload: reloadThroughEdgeAuthGate,
  },
): () => Promise<boolean> {
  let inFlightProbe: Promise<boolean> | null = null;
  let navigating = false;

  return async function recoverThroughEdgeAuthGateIfClosed(): Promise<boolean> {
    // `location.replace` does not stop script execution, so callers that lose the
    // race must stay quiet instead of each re-navigating or surfacing an error.
    if (navigating) {
      return true;
    }

    // A cold load fires many API requests at once. Without coalescing, a shut gate
    // costs one probe and one navigation per in-flight request.
    inFlightProbe ??= isClosed().finally(() => { inFlightProbe = null; });

    if (!await inFlightProbe) {
      return false;
    }

    // Re-checked after the await: every coalesced caller resumes here, and only the
    // first may navigate. Single-threaded from this point to the assignment below,
    // so no caller can observe a stale `false`.
    if (navigating) {
      return true;
    }

    // Refused when this page already came back through a bypass — that loop guard
    // stays authoritative, and the caller must surface the error instead of
    // waiting for a navigation that will not happen.
    navigating = reload();
    return navigating;
  };
}

/**
 * True when the edge auth gate was shut and a reload past it is now under way, so
 * the caller should abandon its request rather than report a failure.
 */
export const recoverThroughEdgeAuthGateIfClosed = createEdgeAuthGateRecovery();

/** Drops the bypass marker from the address bar once the app is up. */
export function clearEdgeAuthGateMarker(): void {
  const cleaned = markerStrippedUrl(window.location.href);

  if (cleaned != null) {
    window.history.replaceState(window.history.state, '', cleaned);
  }
}
